import type {
  BmAdminMonitorFeedback,
  BmAdminMonitorFollowRequest,
  BmAdminMonitorPageRequest,
  BmAdminMonitorPageResponse,
  BmAdminMonitorPageResult,
  BmAdminMonitorQuery,
} from './type/bm-admin-monitor'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'

export const bmAdminMonitorApi = defineApi(
  'operation/bmAdminMonitor',
  ({ prefix }) => {
    /**
     * 查询BM 管理员监控分页列表。
     * POST /gobestads/operation/bmAdminMonitor/queryRecordPage
     */
    function queryRecordPage(
      params: BmAdminMonitorQuery,
    ): Promise<BmAdminMonitorPageResult> {
      const condition = params.condition

      const sortBy: BmAdminMonitorPageRequest['sortBy'] = {
        bmId: 'bmId',
        discoveredAt: 'occurredAt',
        feedbackAt: 'followOperatedAt',
      }[params.sort?.field ?? ''] as BmAdminMonitorPageRequest['sortBy']
      const request: BmAdminMonitorPageRequest = {
        condition: {
          bmId: condition.bmId,
          bmName: condition.bmName,
          dateRange: condition.dateRange,
          eventType:
            condition.changeType === 'promoted'
              ? 'role_promotion'
              : condition.changeType,
          followStatus: condition.feedbackStatus,
          isInternal: condition.isInternal,
          userEmail: condition.userEmail,
        },
        orderBy: params.sort?.order?.toUpperCase() as
          | 'ASC'
          | 'DESC'
          | undefined,
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        sortBy,
      }
      return requestClient
        .post<BmAdminMonitorPageResponse>(prefix('queryRecordPage'), request)
        .then((response) => ({
          items: response.list.map((record) => ({
            bmId: record.bmId,
            bmName: record.bmName,
            changeType:
              record.eventType === 'role_promotion' ? 'promoted' : 'new_admin',
            discoveredAt: record.occurredAt,
            feedbackAt: record.followOperatedAt ?? undefined,
            feedbackBy: record.followOperator,
            feedbackStatus: record.followStatus,
            id: String(record.id),
            isInternal: record.isInternal,
            remark: record.followRemark,
            userEmail: record.userEmail,
            userName: record.userName,
          })),
          total: response.total,
        }))
    }

    /**
     * 提交BM 管理员监控反馈。
     * POST /gobestads/operation/bmAdminMonitor/follow
     * POST /gobestads/operation/bmAdminMonitor/batchFollow
     */
    function follow(ids: string[], feedback: BmAdminMonitorFeedback) {
      const recordIds = ids.map((id) => {
        const recordId = Number(id)
        if (!Number.isInteger(recordId) || recordId < 1) {
          throw new Error(`无效的监控记录ID：${id}`)
        }
        return recordId
      })
      const request: BmAdminMonitorFollowRequest = {
        followRemark: feedback.remark,
        followStatus: feedback.feedbackStatus,
      }
      if (recordIds.length === 1) {
        request.recordId = recordIds[0]
        return requestClient.post(prefix('follow'), request)
      }
      request.recordIds = recordIds
      return requestClient.post(prefix('batchFollow'), request)
    }

    return { follow, queryRecordPage }
  },
)
